/**
 * Course playlist alignment for SoP Phase 1.
 * Lists mirror src/config/SoP Tests - *.csv (Conor Harris Biomechanics course).
 */
import type { RuleDocVersion } from '@/lib/posture-analysis/rule-params';
import type { TestData, TestDataItem } from './types';

export type SopAssessmentMode = 'passive' | 'active';

export type ProcedureVariant = {
  id: string;
  label: string;
  url: string;
  videoId: string;
};

const videoIdFromUrl = (url: string): string => {
  const match = url.match(/(?:v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return match?.[1] ?? '';
};

const item = (
  label: string,
  url: string,
  nominal: number,
  extra: Partial<TestDataItem> = {}
): TestDataItem => ({
  label,
  url,
  videoId: videoIdFromUrl(url),
  nominal,
  unit: '°',
  ...extra,
});

/** In-person passive battery (Foot Presentation excluded — use foot battery toggle). */
export const PASSIVE_COURSE_TEST_DATA: TestData = {
  ShoulderIR: item('Shoulder IR', 'https://www.youtube.com/watch?v=sW9joE4lMdI', 70),
  ShoulderER: item('Shoulder ER', 'https://www.youtube.com/watch?v=Lugzbnwp-uo', 90),
  ShoulderFlexion: item('Shoulder Flexion', 'https://www.youtube.com/watch?v=EO6i06bomTI', 120),
  ShoulderAbduction: item('Shoulder Abduction', 'https://www.youtube.com/watch?v=2-8SkieTgCs', 60, {
    important: true,
  }),
  PelvicRotation: item('Pelvic Rotation', 'https://www.youtube.com/watch?v=uc9xn20EK08', 70, {
    important: true,
  }),
  StraightLegRaise: item('Straight Leg Raise', 'https://www.youtube.com/watch?v=IphzcSive7Q', 90),
  HipExtension: item('Hip Extension', 'https://www.youtube.com/watch?v=fLiyzmy6SUA', 0, {
    nominalDisplay: '0° (Full Ext.)',
    allowsNegative: true,
  }),
  HipAbduction: item('Hip Abduction', 'https://www.youtube.com/watch?v=gUj8559m_tE', 45),
  FemoralIR: item('Femoral IR', 'https://www.youtube.com/watch?v=QV9ABYw1IPw', 40),
  FemoralER: item('Femoral ER', 'https://www.youtube.com/watch?v=QV9ABYw1IPw', 45),
  GroundingPerformance: item(
    'Grounding Performance',
    'https://www.youtube.com/watch?v=ClgTssTCmOw',
    8,
    { type: 'grounding', important: true }
  ),
  CervicalAxialRotation: item(
    'Cervical Axial Rotation',
    'https://www.youtube.com/watch?v=B4B9_9ZXfbw',
    30,
    { important: true, optional: true }
  ),
  CervicalLateralFlexion: item(
    'Cervical Lateral Flexion',
    'https://www.youtube.com/watch?v=wR8oHDqokcE',
    30,
    { optional: true }
  ),
  // Legacy Ober's — only surfaced in legacy RuleDoc mode
  HipAdductionObers: {
    label: "Hip Adduction (Ober's)",
    url: 'https://www.youtube.com/watch?v=OxVTRYnsp5Y',
    videoId: 'OxVTRYnsp5Y',
    nominal: 1,
    nominalDisplay: 'Knee to Table',
    type: 'select',
    options: [
      'Select...',
      '+2 (Pass) Full Extension, Knee to Table, Better Side',
      '+1 (Pass) Full Extension, Knee to Table',
      '0 (Fail) Full Extension',
      '-1 degrees to Full Extension (Fail)',
      '-2 degrees to Full Extension (Fail)',
      '-3 degrees to Full Extension (Fail)',
      '-5 degrees to Full Extension (Fail)',
      '-10 degrees to Full Extension (Fail)',
      '-15 degrees to Full Extension (Fail)',
      '-20 degrees to Full Extension (Fail)',
      '-25 degrees to Full Extension (Fail)',
      '-30 degrees to Full Extension (Fail)',
      '-35 degrees to Full Extension (Fail)',
      '-40 degrees to Full Extension (Fail)',
      '-45 degrees to Full Extension (Fail)',
      '-50 degrees to Full Extension (Fail)',
    ],
  },
  /** Kept for saved assessments; not in default passive layout. */
  FootPresentation: {
    label: 'Foot Presentation',
    url: 'https://www.youtube.com/watch?v=5pJQzuUTQpw',
    videoId: '5pJQzuUTQpw',
    nominal: 'R Supinated L Pronated',
    dualGoniometer: true,
    type: 'select',
    options: [
      'Select...',
      'R Supinated L Pronated',
      'R>L Supinated',
      'L>R Supinated',
      'R Pronated L Supinated',
      'R>L Pronated',
      'L>R Pronated',
      'Not Sure',
    ],
  },
};

const HIP_EXTENSION_ACTIVE_VARIANTS: ProcedureVariant[] = [
  {
    id: 'standing',
    label: 'Standing',
    url: 'https://www.youtube.com/watch?v=2OH8D_OeUTw',
    videoId: '2OH8D_OeUTw',
  },
  {
    id: 'sidelying',
    label: 'Sidelying',
    url: 'https://www.youtube.com/watch?v=yAos3qIRA8s',
    videoId: 'yAos3qIRA8s',
  },
];

/** Remote active self-assessments only (active playlist). */
export const ACTIVE_COURSE_TEST_DATA: TestData = {
  ShoulderIR: item('Shoulder IR (Active)', 'https://www.youtube.com/watch?v=G3HUE5GiMFg', 70),
  ShoulderER: item('Shoulder ER (Active)', 'https://www.youtube.com/watch?v=caBgLuyhJq0', 90),
  ShoulderAbduction: item(
    'Shoulder Abduction (Active)',
    'https://www.youtube.com/watch?v=Y_B2LGdsP_s',
    45
  ),
  HipExtension: {
    ...item('Hip Extension (Active)', HIP_EXTENSION_ACTIVE_VARIANTS[0].url, 0, {
      allowsNegative: true,
    }),
    procedureVariants: HIP_EXTENSION_ACTIVE_VARIANTS,
    defaultProcedureVariant: 'standing',
  },
  PelvicRotation: item(
    'Pelvic Rotation (Active)',
    'https://www.youtube.com/watch?v=C_sXCupI5v8',
    70
  ),
  FemoralIR: item('Femoral IR (Active)', 'https://www.youtube.com/watch?v=MIXIh6M5-Ss', 40),
  FemoralER: item('Femoral ER (Active)', 'https://www.youtube.com/watch?v=MIXIh6M5-Ss', 45),
  HipAbduction: item('Hip Abduction (Active)', 'https://www.youtube.com/watch?v=pksdlUA9als', 45),
  StraightLegRaise: item('SLR Self-Assessment', 'https://www.youtube.com/watch?v=XyoFUr2MQgM', 50),
};

/** Experimental foot battery (in-person); appended to passive when toggle on. */
export const FOOT_BATTERY_TEST_DATA: TestData = {
  FootPronation: item('Foot Pronation', 'https://www.youtube.com/watch?v=5pJQzuUTQpw', 45),
  EarlyFootSupination: item('Early Foot Supination', 'https://www.youtube.com/watch?v=nB24FccMPiI', 45),
  LateFootSupination: item('Late Foot Supination', 'https://www.youtube.com/watch?v=WdhReqXEGxM', 45),
  BigToeExtension: item('Big Toe Extension', 'https://www.youtube.com/watch?v=_Ez8mhADWHg', 45),
  SeatedTibialRotation: item(
    'Seated Tibial Rotation',
    'https://www.youtube.com/watch?v=cadNEWGOK3Q',
    45
  ),
  SupineKneeFlexion: item('Supine Knee Flexion', 'https://www.youtube.com/watch?v=-iGYZ2t8E38', 45),
  ProneKneeFlexion: item('Prone Knee Flexion', 'https://www.youtube.com/watch?v=Vp_YC1ChMZo', 45),
  FootKneeExtension: item('Knee Extension', 'https://www.youtube.com/watch?v=uPT6RCy5Z7w', 45),
  ToeFlexionDynamometer: item(
    'Toe Flexion (Dynamometer)',
    'https://www.youtube.com/watch?v=BGS5LbEBhN4',
    8,
    { type: 'grounding' }
  ),
};

/** Passive playlist order (matches PassiveTests.csv). No Foot Presentation. */
export const PASSIVE_COURSE_LAYOUT_ORDER: (string | string[])[] = [
  // Optional cervical battery — hidden until toolbar "Optional" toggle (TestInputGrid).
  ['CervicalAxialRotation', 'CervicalLateralFlexion'],
  ['ShoulderIR', 'ShoulderER'],
  'ShoulderFlexion',
  'ShoulderAbduction',
  'PelvicRotation',
  'StraightLegRaise',
  ['HipExtension', 'HipAbduction'],
  ['FemoralIR', 'FemoralER'],
  'GroundingPerformance',
];

export const PASSIVE_COURSE_LAYOUT_LEGACY: (string | string[])[] = [
  ['ShoulderIR', 'ShoulderER'],
  'ShoulderFlexion',
  'ShoulderAbduction',
  'PelvicRotation',
  'StraightLegRaise',
  ['HipAdductionObers', 'HipAbduction'],
  'GroundingPerformance',
  ['FemoralIR', 'FemoralER'],
];

/** Active playlist order (matches ActiveTests.csv). */
export const ACTIVE_COURSE_LAYOUT_ORDER: (string | string[])[] = [
  ['ShoulderIR', 'ShoulderER'],
  'ShoulderAbduction',
  'HipExtension',
  'PelvicRotation',
  ['FemoralIR', 'FemoralER'],
  'HipAbduction',
  'StraightLegRaise',
];

export const FOOT_BATTERY_LAYOUT_ORDER: (string | string[])[] = [
  'FootPronation',
  ['EarlyFootSupination', 'LateFootSupination'],
  'BigToeExtension',
  'SeatedTibialRotation',
  ['SupineKneeFlexion', 'ProneKneeFlexion'],
  'FootKneeExtension',
  'ToeFlexionDynamometer',
];

const cloneTestData = (data: TestData): TestData =>
  Object.fromEntries(Object.entries(data).map(([key, value]) => [key, { ...value }]));

export type LayoutOrderOptions = {
  mode: SopAssessmentMode;
  footBatteryEnabled?: boolean;
};

export const getCourseLayoutOrder = (
  version: RuleDocVersion,
  options: LayoutOrderOptions
): (string | string[])[] => {
  if (options.mode === 'active') {
    return ACTIVE_COURSE_LAYOUT_ORDER.map((entry) =>
      Array.isArray(entry) ? [...entry] : entry
    );
  }

  const base =
    version === 'legacy'
      ? PASSIVE_COURSE_LAYOUT_LEGACY.map((entry) => (Array.isArray(entry) ? [...entry] : entry))
      : PASSIVE_COURSE_LAYOUT_ORDER.map((entry) => (Array.isArray(entry) ? [...entry] : entry));

  if (!options.footBatteryEnabled) {
    return base;
  }

  return [...base, ...FOOT_BATTERY_LAYOUT_ORDER.map((entry) => (Array.isArray(entry) ? [...entry] : entry))];
};

export const getCourseTestDataMap = (
  version: RuleDocVersion,
  options: LayoutOrderOptions
): TestData => {
  if (options.mode === 'active') {
    return cloneTestData(ACTIVE_COURSE_TEST_DATA);
  }

  const passive = cloneTestData(PASSIVE_COURSE_TEST_DATA);

  if (version === 'legacy') {
    delete passive.HipExtension;
    if (passive.CervicalAxialRotation) passive.CervicalAxialRotation.optional = false;
    if (passive.CervicalLateralFlexion) passive.CervicalLateralFlexion.optional = false;
  }

  if (options.footBatteryEnabled) {
    return { ...passive, ...cloneTestData(FOOT_BATTERY_TEST_DATA) };
  }

  return passive;
};

/** Keys visible on the assessment page for the current mode/toggles. */
export const getVisibleCourseTestKeys = (
  version: RuleDocVersion,
  options: LayoutOrderOptions & { showOptionalAssessments?: boolean }
): string[] => {
  const layout = getCourseLayoutOrder(version, options);
  const keys: string[] = [];
  for (const entry of layout) {
    const group = Array.isArray(entry) ? entry : [entry];
    for (const key of group) {
      const item = getCourseTestDataMap(version, options)[key];
      if (item?.optional && options.showOptionalAssessments === false) continue;
      if (key === 'FootPresentation' || key === 'HipAdductionObers') continue;
      if (key === 'CervicalAxialRotation' || key === 'CervicalLateralFlexion') continue;
      keys.push(key);
    }
  }
  return keys;
};

export type SopPdfExportRow = {
  key: string;
  label: string;
  left: string;
  right: string;
  goal: string;
};

const formatGoalForPdf = (item: TestDataItem): string => {
  if (item.type === 'grounding') return 'No Bias';
  if (item.type === 'select') return String(item.nominal ?? '—');
  if (item.nominalDisplay) return item.nominalDisplay;
  const unit = item.unit ?? '°';
  return `${item.nominal}${unit}`;
};

const readSideValue = (assessment: Record<string, unknown>, side: 'L' | 'R', testKey: string): string => {
  const raw = assessment[`${side}_${testKey}`];
  if (raw === null || raw === undefined || raw === '') return 'N/A';
  return String(raw);
};

/** Rows for SOP PDF export — follows saved assessment mode and course playlists. */
export const getSopPdfExportRows = (
  assessment: Record<string, unknown>,
  version: RuleDocVersion,
  options: LayoutOrderOptions & { showOptionalAssessments?: boolean }
): SopPdfExportRow[] => {
  const mode = options.mode;
  const dataMap = getCourseTestDataMap(version, options);
  const keys = getVisibleCourseTestKeys(version, options);

  return keys.map((key) => {
    const item = dataMap[key];
    if (!item) {
      return { key, label: key, left: 'N/A', right: 'N/A', goal: '—' };
    }

    let label = item.label;
    if (key === 'HipExtension' && mode === 'active') {
      const variantId =
        (typeof assessment.HipExtension_procedureVariant === 'string'
          ? assessment.HipExtension_procedureVariant
          : 'standing') || 'standing';
      const variant = item.procedureVariants?.find((v) => v.id === variantId);
      if (variant) {
        label = `${label} (${variant.label})`;
      }
    }

    return {
      key,
      label,
      left: readSideValue(assessment, 'L', key),
      right: readSideValue(assessment, 'R', key),
      goal: formatGoalForPdf(item),
    };
  });
};

export const resolveAssessmentTestMode = (assessment: Record<string, unknown>): SopAssessmentMode =>
  assessment.testMode === 'active' ? 'active' : 'passive';
